import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import type {
  BulkCreatePhotoPostsInput,
  BulkCreatePhotoPostsResult,
  ListParticipantsInput,
  ListParticipantsResult,
  ParticipantSummary,
} from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * 写真まとめて一括投稿ツールの client hooks (issue #23、Phase B)。
 * `useDistributeVerification` と同 pattern (idle/pending/success/error)。
 */

export type ListParticipantsState =
  | { status: 'idle'; participants: []; error: null }
  | { status: 'loading'; participants: []; error: null }
  | { status: 'ready'; participants: ParticipantSummary[]; error: null }
  | { status: 'error'; participants: []; error: Error };

export interface UseListParticipantsReturn {
  state: ListParticipantsState;
  load: (districtId: string) => Promise<ParticipantSummary[] | null>;
  reset: () => void;
}

/** 地区の参加者一覧を取得 (投稿者割当ピッカー用)。 */
export function useListParticipants(): UseListParticipantsReturn {
  const [state, setState] = useState<ListParticipantsState>({
    status: 'idle',
    participants: [],
    error: null,
  });

  const load = useCallback(async (districtId: string): Promise<ParticipantSummary[] | null> => {
    setState({ status: 'loading', participants: [], error: null });
    try {
      const fn = httpsCallable<ListParticipantsInput, ListParticipantsResult>(
        functions,
        'listParticipants',
      );
      const res = await fn({ districtId });
      const participants = res.data.participants ?? [];
      setState({ status: 'ready', participants, error: null });
      return participants;
    } catch (e) {
      setState({ status: 'error', participants: [], error: e as Error });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', participants: [], error: null });
  }, []);

  return { state, load, reset };
}

export type BulkCreateState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: BulkCreatePhotoPostsResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseBulkCreatePhotoPostsReturn {
  state: BulkCreateState;
  call: (input: BulkCreatePhotoPostsInput) => Promise<BulkCreatePhotoPostsResult | null>;
  reset: () => void;
}

/** 参加者名義で写真→投稿を一括作成 (dryRun / 本実行)。 */
export function useBulkCreatePhotoPosts(): UseBulkCreatePhotoPostsReturn {
  const [state, setState] = useState<BulkCreateState>({
    status: 'idle',
    result: null,
    error: null,
  });

  const call = useCallback(
    async (input: BulkCreatePhotoPostsInput): Promise<BulkCreatePhotoPostsResult | null> => {
      setState({ status: 'pending', result: null, error: null });
      try {
        // issue #28: 既定 70 秒だと最大 1000 件 + 重い著者の重複判定で稀に打ち切られるため、
        // server timeout (300s) を上回る 9 分を明示し、十分な時間を確保する。
        const fn = httpsCallable<BulkCreatePhotoPostsInput, BulkCreatePhotoPostsResult>(
          functions,
          'bulkCreatePhotoPosts',
          { timeout: 540_000 },
        );
        const res = await fn(input);
        setState({ status: 'success', result: res.data, error: null });
        return res.data;
      } catch (e) {
        setState({ status: 'error', result: null, error: e as Error });
        return null;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setState({ status: 'idle', result: null, error: null });
  }, []);

  return { state, call, reset };
}
